import React, { useState, useMemo, useEffect } from "react";
import TaskFormModal from "./TaskFormModal";
import { CustomIcon } from "../shared/CustomIcon";
import EmptyState from "../shared/EmptyState";
import { formatDate } from "../../lib/format";
import { apiCreateTask, apiUpdateTask, apiDeleteTask } from "./api";
import { apiSetAppointmentStatus } from "../appointments/api";

// Advance a YYYY-MM-DD string forward by the requested cadence. Returns a new
// YYYY-MM-DD string. Caller is expected to have validated `repeat`.
function advanceDate(yyyyMmDd, repeat) {
  if (!yyyyMmDd) return "";
  const d = new Date(`${yyyyMmDd}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  if (repeat === "daily") d.setDate(d.getDate() + 1);
  else if (repeat === "weekly") d.setDate(d.getDate() + 7);
  else if (repeat === "monthly") d.setMonth(d.getMonth() + 1);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const LISTS = ["Personal", "Ministry", "Follow-ups", "Errands"];

// TasksPage — "My Tasks" + "Appointments" tabs. Appointments auto-populate
// from the appointments storage (read-only view filtered to the current user).
export default function TasksPage({ currentUser, tasks, setTasks, appointments, setAppointments, users, navigate }) {
  const [tab, setTab] = useState("tasks"); // 'tasks' | 'appointments'
  const [showForm, setShowForm] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [prefillDueDate, setPrefillDueDate] = useState("");
  const [search, setSearch] = useState("");

  // Auto-open the New Task form when arriving with ?new=1 (e.g. from the
  // Calendar click-to-add Task option). Optional &date=YYYY-MM-DD prefills due.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("new") === "1") {
      const d = sp.get("date") || "";
      setEditTask(null);
      setPrefillDueDate(d);
      setShowForm(true);
      sp.delete("new"); sp.delete("date");
      const qs = sp.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${qs ? "?" + qs : ""}`);
    }
  }, []);
  const [grouped, setGrouped] = useState(false);
  const [apptView, setApptView] = useState("list"); // 'list' | 'day'

  const myTasks = useMemo(() => tasks.filter(t => t && t.userId === currentUser.id), [tasks, currentUser.id]);

  const matchesSearch = (t) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (t.title || "").toLowerCase().includes(q) || (t.description || "").toLowerCase().includes(q) || (t.list || "").toLowerCase().includes(q);
  };
  const activeTasks = useMemo(() => myTasks.filter(t => !t.done).filter(matchesSearch).sort((a, b) => {
    if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate);
    if (a.dueDate) return -1; if (b.dueDate) return 1;
    return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
  }), [myTasks, search]);
  const doneTasks = useMemo(() => myTasks.filter(t => t.done).filter(matchesSearch).sort((a, b) => new Date(b.completedAt || 0) - new Date(a.completedAt || 0)), [myTasks, search]);

  // For grouped view — group active tasks by list label
  const activeByList = useMemo(() => {
    const map = new Map();
    activeTasks.forEach(t => {
      const key = t.list || "Personal";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    });
    return map;
  }, [activeTasks]);

  const myAppts = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return appointments
      .filter(a => a && (a.userId === currentUser.id || a.pastorId === currentUser.id))
      .filter(a => !a.date || new Date(a.date) >= today || a.status === "pending")
      .sort((a, b) => (a.date || "").localeCompare(b.date || "") || (a.time || "").localeCompare(b.time || ""));
  }, [appointments, currentUser.id]);

  // Day-timeline grouping for appointments
  const apptsByDay = useMemo(() => {
    const map = new Map();
    myAppts.forEach(a => {
      const key = a.date || "__no_date__";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    });
    return map;
  }, [myAppts]);

  const toggleDone = async (t) => {
    try {
      const next = !t.done;
      const updated = await apiUpdateTask(t.id, { done: next });
      setTasks(prev => prev.map(x => x.id === t.id ? updated : x));
      // Recurring auto-generation: when a repeating task is marked DONE with a due date,
      // automatically create the next occurrence so the user always has the next reminder.
      if (next && t.repeat && t.repeat !== "none" && t.dueDate) {
        const nextDue = advanceDate(t.dueDate, t.repeat);
        if (nextDue) {
          try {
            const newTask = await apiCreateTask({
              title: t.title,
              description: t.description || "",
              dueDate: nextDue,
              repeat: t.repeat,
              list: t.list || "Personal",
            });
            setTasks(prev => [...prev, newTask]);
          } catch (_) { /* non-blocking */ }
        }
      }
    } catch (e) { alert(e.message || "Could not update task"); }
  };
  const saveTask = async (data) => {
    try {
      if (editTask) {
        const updated = await apiUpdateTask(editTask.id, data);
        setTasks(prev => prev.map(x => x.id === editTask.id ? updated : x));
      } else {
        const created = await apiCreateTask(data);
        setTasks(prev => [...prev, created]);
      }
      setEditTask(null);
      return true;
    } catch (e) { alert(e.message || "Could not save"); return false; }
  };
  const deleteTask = async (id) => {
    try {
      await apiDeleteTask(id);
      setTasks(prev => prev.filter(x => x.id !== id));
      setShowForm(false); setEditTask(null);
    } catch (e) { alert(e.message || "Could not delete"); }
  };
  const setApptStatus = async (a, status) => {
    try {
      const updated = await apiSetAppointmentStatus(a.id, status);
      setAppointments(prev => prev.map(x => x.id === a.id ? updated : x));
    } catch (e) { alert(e.message || "Could not update appointment"); }
  };

  const today = new Date().toISOString().split("T")[0];
  const userNameById = (id) => {
    if (!id) return "";
    const u = users.find(x => x.id === id);
    return u?.name || u?.username || id;
  };

  const renderTaskRow = (t) => {
    const overdue = t.dueDate && t.dueDate < today;
    const done = !!t.done;
    // Semantic Card variant — Issue 4 (Phase 6).
    const variant = done ? "green" : (overdue ? "red" : "blue");
    return (
      <div key={t.id} className={`task-row card-${variant} ${done ? "is-done" : ""}`} data-testid={`task-row-${t.id}`}>
        <button
          type="button"
          className={`task-check ${done ? "checked" : ""}`}
          data-testid={`task-check-${t.id}`}
          aria-label={done ? "Mark active" : "Mark complete"}
          onClick={() => toggleDone(t)}
        >{done ? <CustomIcon name="check" size={12} /> : ""}</button>
        <div className="task-main" onClick={() => { setEditTask(t); setShowForm(true); }}>
          <div className="task-title">{t.title}</div>
          <div className="task-meta">
            {!done && t.dueDate && <span className={`tm-pill ${overdue ? "overdue" : ""}`} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><CustomIcon name="calendar" size={12} /> {formatDate(t.dueDate)}{overdue ? " — overdue" : ""}</span>}
            {!done && t.repeat && t.repeat !== "none" && <span className="tm-pill repeat" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><CustomIcon name="recurring" size={12} /> {t.repeat}</span>}
            {!done && <span className="tm-pill">{t.list || "Personal"}</span>}
            {done && t.completedAt && <span className="tm-pill">Completed {formatDate(t.completedAt)}</span>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="tasks-page">
      <div style={{ fontFamily: "'Cinzel',serif", fontSize: 22, fontWeight: 700, color: "var(--kc-text)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 16 }}>
        Tasks
      </div>

      <div className="checkins-tabs" role="tablist">
        <button type="button" role="tab" data-testid="tasks-tab-my" className={`checkins-tab ${tab === "tasks" ? "active" : ""}`} aria-selected={tab === "tasks"} onClick={() => setTab("tasks")}>
          My Tasks {activeTasks.length > 0 && <span style={{ marginLeft: 4, opacity: 0.65 }}>({activeTasks.length})</span>}
        </button>
        <button type="button" role="tab" data-testid="tasks-tab-appts" className={`checkins-tab ${tab === "appointments" ? "active" : ""}`} aria-selected={tab === "appointments"} onClick={() => setTab("appointments")}>
          Appointments {myAppts.length > 0 && <span style={{ marginLeft: 4, opacity: 0.65 }}>({myAppts.length})</span>}
        </button>
      </div>

      {tab === "tasks" && (
        <>
          {/* Search bar + group toggle */}
          <div data-testid="tasks-toolbar" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
            <input
              type="search"
              data-testid="tasks-search"
              className="form-input"
              placeholder="Search tasks…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: 200 }}
            />
            <button
              type="button"
              data-testid="tasks-group-toggle"
              onClick={() => setGrouped(g => !g)}
              style={{
                padding: "6px 12px", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
                textTransform: "uppercase", borderRadius: 999, cursor: "pointer",
                fontFamily: "'Montserrat',sans-serif", flexShrink: 0,
                background: grouped ? "var(--kc-accent)" : "transparent",
                color: grouped ? "#1A1A1A" : "var(--kc-accent)",
                border: `1.5px solid ${grouped ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 40%, transparent)"}`,
              }}
            >{grouped ? "Grouped" : "Flat list"}</button>
          </div>

          <div data-testid="my-tasks-list">
            {activeTasks.length === 0 && doneTasks.length === 0 ? (
              <EmptyState
                data-testid="tasks-empty"
                icon="check"
                title={search ? "No tasks match your search" : "No tasks yet"}
                subtitle={search
                  ? "Try a different search term or clear the box."
                  : "Capture a follow-up call, a sermon prep reminder, or anything else on your plate."}
                action={!search ? {
                  label: "+ New Task",
                  onClick: () => { setEditTask(null); setShowForm(true); },
                } : undefined}
              />
            ) : (
              <>
                {activeTasks.length > 0 && (
                  <>
                    <div className="tasks-section-label">Active</div>
                    {grouped ? (
                      LISTS.map(listName => {
                        const items = activeByList.get(listName) || [];
                        if (items.length === 0) return null;
                        return (
                          <div key={listName} data-testid={`tasks-group-${listName}`} style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: "color-mix(in srgb, var(--kc-accent) 85%, transparent)", padding: "8px 0 4px", fontFamily: "'Montserrat',sans-serif" }}>{listName} <span style={{ opacity: 0.55 }}>({items.length})</span></div>
                            {items.map(renderTaskRow)}
                          </div>
                        );
                      })
                    ) : (
                      activeTasks.map(renderTaskRow)
                    )}
                  </>
                )}
                {doneTasks.length > 0 && (
                  <>
                    <div className="tasks-section-label" data-testid="tasks-section-completed">Completed ({doneTasks.length})</div>
                    {doneTasks.slice(0, 20).map(renderTaskRow)}
                  </>
                )}
              </>
            )}
          </div>
        </>
      )}

      {tab === "appointments" && (
        <>
          {/* View toggle for the appointments tab */}
          <div data-testid="appts-toolbar" style={{ display: "flex", gap: 6, marginBottom: 12 }}>
            <button
              type="button"
              data-testid="appts-view-list"
              onClick={() => setApptView("list")}
              style={{
                padding: "6px 14px", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
                textTransform: "uppercase", borderRadius: 999, cursor: "pointer",
                fontFamily: "'Montserrat',sans-serif",
                background: apptView === "list" ? "var(--kc-accent)" : "transparent",
                color: apptView === "list" ? "#1A1A1A" : "var(--kc-accent)",
                border: `1.5px solid ${apptView === "list" ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 40%, transparent)"}`,
              }}
            >List</button>
            <button
              type="button"
              data-testid="appts-view-day"
              onClick={() => setApptView("day")}
              style={{
                padding: "6px 14px", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
                textTransform: "uppercase", borderRadius: 999, cursor: "pointer",
                fontFamily: "'Montserrat',sans-serif",
                background: apptView === "day" ? "var(--kc-accent)" : "transparent",
                color: apptView === "day" ? "#1A1A1A" : "var(--kc-accent)",
                border: `1.5px solid ${apptView === "day" ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 40%, transparent)"}`,
              }}
            >Day timeline</button>
          </div>

          <div data-testid="my-appts-list">
            {myAppts.length === 0 ? (
              <div className="widget-empty-add">
                <div className="wea-icon"><CustomIcon name="calendar-clock" size={40} /></div>
                <div className="wea-title">No upcoming appointments</div>
                <div className="wea-sub">Booked appointments will appear here automatically. Use the Appointments page to schedule one.</div>
                <button className="btn btn-outline" data-testid="tasks-open-appts" onClick={() => navigate("/appointments")}>Manage Appointments</button>
              </div>
            ) : apptView === "day" ? (
              // Day-timeline view: appointments grouped by date, vertical timeline of times.
              <div data-testid="appts-day-timeline" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                {Array.from(apptsByDay.entries()).map(([dayKey, dayAppts]) => (
                  <div key={dayKey} data-testid={`appts-day-${dayKey}`} className="card" style={{ padding: 14 }}>
                    <div style={{ fontFamily: "'Cinzel',serif", fontSize: 13, color: "var(--kc-accent)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12, fontWeight: 700 }}>
                      {dayKey === "__no_date__" ? "Undated" : formatDate(dayKey)}
                    </div>
                    <div style={{ position: "relative", paddingLeft: 70 }}>
                      <div style={{ position: "absolute", left: 60, top: 0, bottom: 0, width: 2, background: "color-mix(in srgb, var(--kc-accent) 20%, transparent)" }} />
                      {dayAppts.map(a => {
                        const isMyInbound = a.pastorId === currentUser.id && a.userId !== currentUser.id;
                        const otherName = isMyInbound ? (a.userName || userNameById(a.userId)) : userNameById(a.pastorId);
                        return (
                          <div key={a.id} data-testid={`appts-timeline-row-${a.id}`} style={{ position: "relative", padding: "10px 0 14px", display: "flex", alignItems: "flex-start", gap: 14 }}>
                            <div style={{ position: "absolute", left: -70, top: 12, width: 56, fontSize: 12, fontWeight: 700, color: "var(--kc-accent)", fontFamily: "'Montserrat',sans-serif", letterSpacing: "0.04em", textAlign: "right" }}>{a.time || "—"}</div>
                            <span style={{ position: "absolute", left: -10, top: 14, width: 12, height: 12, borderRadius: "50%", background: a.status === "confirmed" ? "#3F7D58" : a.status === "pending" ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 30%, transparent)", border: "2px solid #0F0F0F" }} />
                            <div
                              role="link"
                              tabIndex={0}
                              onClick={() => navigate(`/appointments?focus=${a.id}`)}
                              onKeyDown={e => { if (e.key === "Enter") navigate(`/appointments?focus=${a.id}`); }}
                              style={{ flex: 1, cursor: "pointer", padding: "6px 10px", borderRadius: 8, background: "color-mix(in srgb, var(--kc-accent) 6%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)" }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                <div className="appt-title" style={{ flex: 1, minWidth: 0 }}>{a.title || "Appointment"}</div>
                                <span className={`appt-status ${a.status}`} data-testid={`appt-status-${a.id}`}>{a.status}</span>
                              </div>
                              <div className="appt-meta" style={{ marginTop: 2 }}>{otherName ? `With ${otherName}` : ""}{a.reason ? ` — ${a.reason}` : ""}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Original list view
              <>
                {myAppts.map(a => {
                  const d = a.date ? new Date(a.date) : null;
                  const isMyInbound = a.pastorId === currentUser.id && a.userId !== currentUser.id;
                  const otherName = isMyInbound ? (a.userName || userNameById(a.userId)) : userNameById(a.pastorId);
                  return (
                    <div
                      key={a.id}
                      className="appt-row clickable"
                      data-testid={`appt-row-${a.id}`}
                      role="link"
                      tabIndex={0}
                      onClick={() => navigate(`/appointments?focus=${a.id}`)}
                      onKeyDown={e => { if (e.key === "Enter") navigate(`/appointments?focus=${a.id}`); }}
                    >
                      <div className="appt-row-top">
                        <div className="appt-date-box">
                          <div className="a-month">{d ? d.toLocaleDateString("en-US", { month: "short" }) : "—"}</div>
                          <div className="a-day">{d ? d.getDate() : "—"}</div>
                        </div>
                        <div className="appt-main">
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <div className="appt-title" style={{ flex: 1, minWidth: 0 }}>{a.title || "Appointment"}</div>
                            <span className={`appt-status ${a.status}`} data-testid={`appt-status-${a.id}`}>{a.status}</span>
                          </div>
                          <div className="appt-meta">
                            {a.time ? `${a.time} · ` : ""}{otherName ? `With ${otherName}` : ""}
                          </div>
                          {a.reason && <div className="appt-meta" style={{ marginTop: 2 }}>{a.reason}</div>}
                        </div>
                      </div>
                      {(isMyInbound && a.status === "pending") || (a.status !== "cancelled" && (a.userId === currentUser.id || a.pastorId === currentUser.id)) ? (
                        <div className="appt-row-actions" onClick={e => e.stopPropagation()}>
                          {isMyInbound && a.status === "pending" && (
                            <button className="btn btn-sm btn-gold" data-testid={`appt-confirm-${a.id}`} onClick={() => setApptStatus(a, "confirmed")}>Confirm</button>
                          )}
                          {a.status !== "cancelled" && (a.userId === currentUser.id || a.pastorId === currentUser.id) && (
                            <button className="btn btn-sm btn-outline" data-testid={`appt-cancel-${a.id}`} onClick={() => { if (window.confirm("Cancel this appointment?")) setApptStatus(a, "cancelled"); }}>Cancel</button>
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </>
      )}

      <button type="button" className="fab-btn" data-testid="tasks-fab" aria-label="New task" onClick={() => { setEditTask(null); setShowForm(true); }}>+</button>

      {showForm && (
        <TaskFormModal
          existing={editTask || (prefillDueDate ? { dueDate: prefillDueDate } : null)}
          onSave={saveTask}
          onClose={() => { setShowForm(false); setEditTask(null); setPrefillDueDate(""); }}
          onDelete={editTask ? deleteTask : null}
        />
      )}
    </div>
  );
}
